import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeSlash, CheckCircle, Storefront } from "@phosphor-icons/react";

function passwordStrength(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

export default function Register() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { t } = useI18n();
  // VAGUE 35 — read ?ref=TELEPRO-CODE from URL and auto-fill the field.
  // Telepros share links like /inscription?ref=DAMIEN-002 so the prospect
  // never has to type a code they often misremember.
  const [sp] = useSearchParams();
  const storedRef = (() => { try { return localStorage.getItem("mc68_telepro_ref") || ""; } catch { return ""; } })();
  const refParam = (sp.get("ref") || sp.get("telepro") || sp.get("tp") || storedRef).trim().toUpperCase();
  const claimSlug = (sp.get("claim") || "").trim();
  const promoParam = (sp.get("promo") || "").trim().toUpperCase();
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", date_of_birth: "",
    email: "", password: "", password_confirm: "",
    promo_code: promoParam,
    telepro_code: refParam,
    accept: false,
    // Honeypot — bots fill invisible fields; humans don't
    hp_website: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [teleproInfo, setTeleproInfo] = useState(null); // {found, code, name}

  const s = passwordStrength(form.password);

  // Live telepro code lookup (debounced)
  useEffect(() => {
    const code = (form.telepro_code || "").trim().toUpperCase();
    if (!code) { setTeleproInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/public/telepro/lookup", { params: { code } });
        setTeleproInfo(r.data);
      } catch { setTeleproInfo(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.telepro_code]);

  const strengthLabel = [t.register.strength_0, t.register.strength_1, t.register.strength_2, t.register.strength_3, t.register.strength_4][s];
  const strengthColor = ["bg-destructive", "bg-destructive/70", "bg-secondary", "bg-secondary/80", "bg-primary"][s];
  const pwMatch = form.password && form.password === form.password_confirm;
  const canSubmit = form.email.includes("@") && s >= 2 && pwMatch && form.first_name.trim() && form.last_name.trim() && form.accept && !busy;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Honeypot: if filled, silently pretend success but do nothing
    if (form.hp_website) { toast.success(t.register.welcome); navigate("/", { replace: true }); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.accept; delete payload.hp_website;
      if (!payload.business_name) delete payload.business_name;
      if (!payload.date_of_birth) delete payload.date_of_birth;
      if (!payload.promo_code) delete payload.promo_code;
      if (!payload.telepro_code) delete payload.telepro_code;
      const r = await api.post("/auth/register", payload);
      loginWithToken(r.data.token, r.data.user);
      toast.success(t.register.welcome);
      if (r.data.promo_accepted) toast.success(`Code ${r.data.promo_accepted} ✓`);
      try { localStorage.removeItem("mc68_telepro_ref"); } catch { /* noop */ }
      navigate(claimSlug ? `/entreprise/${claimSlug}?claim=1` : "/dashboard?welcome=1", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Pydantic returns an array of validation errors — translate common
      // ones to friendly French so the toast reads like we wrote it.
      let friendly = typeof detail === "string" ? detail : null;
      if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        const field = (first?.loc || []).slice(-1)[0];
        const t = first?.type || "";
        if (field === "email") friendly = "Email invalide — vérifiez l'orthographe (exemple : prenom@email.fr).";
        else if (field === "password" && t === "string_too_short") friendly = "Mot de passe trop court — il doit contenir au moins 8 caractères.";
        else if (field === "password_confirm") friendly = "Les deux mots de passe ne correspondent pas.";
        else if (field === "first_name" || field === "last_name") friendly = "Prénom et nom sont obligatoires.";
        else friendly = first?.msg || "Champ invalide — vérifiez le formulaire.";
      }
      toast.error(friendly || "Une erreur est survenue. Réessayez dans un instant.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-xl px-6 py-12 sm:py-16" data-testid="register-page">
      <div className="label-uppercase mb-2 flex items-center gap-2"><Storefront size={12} weight="bold" /> {t.register.kicker}</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-3">{t.register.h1}</h1>
      <p className="text-muted-foreground mb-8">{t.register.sub}</p>

      {/* VAGUE 35 — Welcome banner when the merchant arrived from a telepro share-link */}
      {refParam && teleproInfo?.found && (
        <div className="mb-6 border-l-4 border-primary bg-primary/5 rounded-sm p-4" data-testid="reg-telepro-welcome">
          <div className="font-bold text-sm flex items-center gap-2 text-primary">
            <CheckCircle size={16} weight="fill"/> Bienvenue !
          </div>
          <p className="text-sm text-foreground mt-1">
            <b>{teleproInfo.name}</b> vous accompagne. Son code <span className="font-mono bg-primary/10 text-primary px-1 rounded">{refParam}</span> est déjà pré-rempli — il suffit de remplir le formulaire ci-dessous.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t.register.first_name + " *"} value={form.first_name} onChange={(v) => update("first_name", v)} testid="reg-first-name" autoComplete="given-name" required />
          <Field label={t.register.last_name + " *"} value={form.last_name} onChange={(v) => update("last_name", v)} testid="reg-last-name" autoComplete="family-name" required />
        </div>
        <Field label={t.register.business_name} value={form.business_name} onChange={(v) => update("business_name", v)} testid="reg-business" autoComplete="organization" placeholder={t.register.business_placeholder} />
        <Field label={t.register.date_of_birth} type="date" value={form.date_of_birth} onChange={(v) => update("date_of_birth", v)} testid="reg-dob" autoComplete="bday" />
        <hr className="border-border" />
        <Field label={t.register.email + " *"} type="email" value={form.email} onChange={(v) => update("email", v)} testid="reg-email" autoComplete="email" required />
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1 block">{t.register.password} *</label>
          <div className="relative">
            <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => update("password", e.target.value)} minLength={8} required className="w-full border border-border rounded-sm px-3 py-2.5 pr-10 text-sm bg-background outline-none focus:border-primary" autoComplete="new-password" data-testid="reg-password" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
              {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {form.password && (
            <div className="mt-2" data-testid="reg-password-strength">
              <div className="flex gap-1">{[1,2,3,4].map((i) => <div key={i} className={`h-1 flex-1 rounded-sm ${i <= s ? strengthColor : "bg-border"}`} />)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{strengthLabel} · {t.register.password_min}</div>
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1 block">{t.register.password_confirm} *</label>
          <input type={showPw ? "text" : "password"} value={form.password_confirm} onChange={(e) => update("password_confirm", e.target.value)} required className={`w-full border rounded-sm px-3 py-2.5 text-sm bg-background outline-none ${form.password_confirm ? (pwMatch ? "border-primary" : "border-destructive") : "border-border focus:border-primary"}`} data-testid="reg-password-confirm" autoComplete="new-password" />
          {form.password_confirm && !pwMatch && <div className="text-[11px] text-destructive mt-1">{t.register.password_mismatch}</div>}
          {form.password_confirm && pwMatch && <div className="text-[11px] text-primary mt-1 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> {t.register.password_match}</div>}
        </div>
        <hr className="border-border" />
        <Field label={t.register.promo} value={form.promo_code} onChange={(v) => update("promo_code", v.toUpperCase())} testid="reg-promo" placeholder="Saisissez votre code" />
        <div data-testid="reg-telepro-block">
          <Field label="Code de votre conseiller (si on vous a appelé)" value={form.telepro_code} onChange={(v) => update("telepro_code", v.toUpperCase())} testid="reg-telepro" placeholder="Ex : LISA-001"/>
          {!form.telepro_code && (
            <div className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1" data-testid="reg-telepro-optional-hint">
              <span className="text-primary font-bold">✓</span>
              <span><b className="text-foreground">Optionnel.</b> Laissez vide si vous vous inscrivez seul·e — le code sert uniquement à créditer le téléprospecteur qui vous a contacté.</span>
            </div>
          )}
          {teleproInfo && form.telepro_code && (
            <div className={`text-[11px] mt-1 flex items-center gap-1 ${teleproInfo.found ? "text-primary" : "text-destructive"}`} data-testid="reg-telepro-status">
              {teleproInfo.found ? (<><CheckCircle size={11} weight="fill"/> Conseiller·ère : <b>{teleproInfo.name}</b></>) : "Code inconnu — laissez vide si vous n'en avez pas."}
            </div>
          )}
        </div>
        {/* Honeypot — visually hidden, bots find it */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
          <label>Website <input type="text" tabIndex={-1} autoComplete="off" value={form.hp_website} onChange={(e) => update("hp_website", e.target.value)} /></label>
        </div>
        <label className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed cursor-pointer">
          <input type="checkbox" checked={form.accept} onChange={(e) => update("accept", e.target.checked)} className="mt-1" data-testid="reg-accept" required />
          <span>{t.register.accept_terms} <Link to="/cgu" className="text-primary underline">{t.auth.terms}</Link> {t.auth.and} <Link to="/confidentialite" className="text-primary underline">{t.auth.privacy}</Link>.</span>
        </label>
        <button type="submit" disabled={!canSubmit} className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-6 py-3.5 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="reg-submit">
          {busy ? t.register.submitting : t.register.submit} {!busy && <ArrowRight size={14} weight="bold" />}
        </button>
        <div className="text-center text-sm text-muted-foreground">
          {t.register.already} <Link to="/connexion?as=pro" className="text-primary font-semibold underline">{t.register.login_link}</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, autoComplete, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} autoComplete={autoComplete} placeholder={placeholder} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm bg-background outline-none focus:border-primary" data-testid={testid} />
    </div>
  );
}
